import { useUserPreference } from '@/admin/hooks/use-user-preference'

/**
 * Lock layout for a page of draggable cards: whether the user locked their
 * arrangement, and whether they have seen the arrange tip. Per user, per page,
 * server-backed. Render it with `<PinMyViewBar>` and pass `pinned` to
 * DraggableCardGrid's `locked`.
 */
export type CardsLayout = 'sections' | 'cards'

export type PinnedView = {
  pinned: boolean
  hintSeen: boolean
  togglePin: () => void
  dismissHint: () => void
  /**
   * WPS-146: how the page shows its cards - calm sections that open in full
   * ("sections") or every card in full ("cards"). Only set for pages that
   * offer both; per user, server-backed.
   */
  layout?: CardsLayout
  setLayout?: (layout: CardsLayout) => void
}

export function usePinnedView(
  page: string,
  options: { defaultLayout?: CardsLayout } = {},
): PinnedView {
  // The user's own toggles are re-read on every visit: a cached copy saved
  // just before a reload can miss the latest choice.
  const pinned = useUserPreference<boolean>(`${page}-cards-pinned`, false, { fresh: true })
  const hintSeen = useUserPreference<boolean>(`${page}-cards-hint-seen`, false)
  const layout = useUserPreference<CardsLayout | null>(`${page}-cards-layout`, null, {
    fresh: true,
  })
  const offersLayouts = Boolean(options.defaultLayout)
  return {
    ...(offersLayouts
      ? {
          layout:
            layout.value === 'sections' || layout.value === 'cards'
              ? layout.value
              : options.defaultLayout,
          setLayout: (next: CardsLayout) => layout.setValue(next),
        }
      : {}),
    pinned: pinned.value,
    hintSeen: hintSeen.value,
    togglePin: () => {
      // Pinning proves they found the feature, so the tip retires too.
      hintSeen.setValue(true)
      pinned.setValue(!pinned.value)
    },
    dismissHint: () => hintSeen.setValue(true),
  }
}
